import {
  Action,
  ActionType,
  LoopOnItemsAction,
  RouterAction,
  Subflow,
} from '../actions/action'
import { Connection } from '../connections/connection'
import { FlowVersion } from '../flow-version'
import { flowStructureUtil } from '../util/flow-structure-util'
import { DeleteActionRequest } from './index'

function _deleteAction(
  flowVersion: FlowVersion,
  request: DeleteActionRequest,
): FlowVersion {
  const clonedVersion: FlowVersion = flowVersion

  function deleteFromActions(
    actions: Action[],
    actionName: string,
    currentSubflow?: Subflow,
  ): boolean {
    for (let i = 0; i < actions.length; i++) {
      const action = actions[i]
      if (action.name === actionName) {
        removeConnectionsAndReconnect(actionName, currentSubflow)
        actions.splice(i, 1)
        return true
      }

      if (action.type === ActionType.LOOP_ON_ITEMS && action.subflow?.actions) {
        const found = deleteFromActions(
          action.subflow.actions,
          actionName,
          action.subflow,
        )
        if (found) {
          updateLoopConnectionsAfterDeletion(clonedVersion, action)
          return true
        }
      }

      if (action.type === ActionType.ROUTER && action.children) {
        for (let j = 0; j < action.children.length; j++) {
          const child = action.children[j]
          if (child && child.actions) {
            const found = deleteFromActions(child.actions, actionName, child)
            if (found) {
              updateBranchConnectionsAfterDeletion(
                clonedVersion,
                action.name,
                j,
              )
              return true
            }
          }
        }
      }
    }
    return false
  }

  function removeConnectionsAndReconnect(
    actionName: string,
    currentSubflow?: Subflow,
  ): void {
    const connections = currentSubflow
      ? currentSubflow.connections
      : clonedVersion.connections

    const { incomingSources, outgoingTargets } = findAdjacentConnections(
      connections,
      actionName,
    )

    removeConnectionsForAction(actionName, currentSubflow)

    reconnectAdjacentActions(incomingSources, outgoingTargets, currentSubflow)
  }

  function findAdjacentConnections(
    connections: Connection[],
    actionName: string,
  ): { incomingSources: string[]; outgoingTargets: string[] } {
    const incomingSources = connections
      .filter((conn: Connection) => conn.targetId === actionName)
      .map((conn: Connection) => conn.sourceId)
      .filter((sourceId): sourceId is string => sourceId !== undefined)

    const outgoingTargets = connections
      .filter((conn: Connection) => conn.sourceId === actionName)
      .map((conn: Connection) => conn.targetId)
      .filter((targetId): targetId is string => targetId !== undefined)

    return { incomingSources, outgoingTargets }
  }

  function removeConnectionsForAction(
    actionName: string,
    currentSubflow?: Subflow,
  ): void {
    if (currentSubflow) {
      currentSubflow.connections = currentSubflow.connections.filter(
        (conn: Connection) =>
          conn.sourceId !== actionName && conn.targetId !== actionName,
      )
    } else {
      clonedVersion.connections = clonedVersion.connections.filter(
        (conn: Connection) =>
          conn.sourceId !== actionName && conn.targetId !== actionName,
      )
    }
  }

  function reconnectAdjacentActions(
    sources: string[],
    targets: string[],
    currentSubflow?: Subflow,
  ): void {
    for (const sourceId of sources) {
      for (const targetId of targets) {
        addConnectionIfNotExists(sourceId, targetId, currentSubflow)
      }
    }
  }

  function addConnectionIfNotExists(
    sourceId: string,
    targetId: string,
    currentSubflow?: Subflow,
  ): void {
    const connections = currentSubflow
      ? currentSubflow.connections
      : clonedVersion.connections

    const connectionExists = connections.some(
      (conn: Connection) =>
        conn.sourceId === sourceId && conn.targetId === targetId,
    )

    if (!connectionExists) {
      const newConnection: Connection = { sourceId, targetId }
      if (currentSubflow) {
        currentSubflow.connections.push(newConnection)
      } else {
        clonedVersion.connections.push(newConnection)
      }
    }
  }

  const found = deleteFromActions(clonedVersion.actions, request.names[0])
  if (!found) {
    throw new Error('Action not found')
  }
  return clonedVersion
}

function updateLoopConnectionsAfterDeletion(
  flowVersion: FlowVersion,
  loopAction: LoopOnItemsAction,
): void {
  const firstElementName = flowStructureUtil.getLoopFirstStepName(
    flowVersion,
    loopAction.name,
  )
  const lastElementName = flowStructureUtil.getLoopLastStepName(
    flowVersion,
    loopAction.name,
  )
  removeLoopConnectionsToSubflow(flowVersion, loopAction.name)
  if (loopAction.subflow && loopAction.subflow.actions.length > 0) {
    if (firstElementName) {
      loopAction.subflow.connections.push({
        sourceId: loopAction.name,
        targetId: firstElementName,
      })
    }
    if (lastElementName) {
      loopAction.subflow.connections.push({
        sourceId: lastElementName,
        targetId: loopAction.name,
      })
    }
  }
}

function removeLoopConnectionsToSubflow(
  flowVersion: FlowVersion,
  loopName: string,
): void {
  const loop = flowStructureUtil.getStepOrThrow(loopName, flowVersion)
  if (loop?.type !== ActionType.LOOP_ON_ITEMS || !loop.subflow?.connections) {
    return
  }
  loop.subflow.connections = loop.subflow.connections.filter(
    (conn: Connection) =>
      conn.sourceId !== loopName && conn.targetId !== loopName,
  )
}

function updateBranchConnectionsAfterDeletion(
  flowVersion: FlowVersion,
  routerName: string,
  branchIndex: number,
): void {
  const router = flowStructureUtil.getStepOrThrow(
    routerName,
    flowVersion,
  ) as RouterAction
  if (!router.children || branchIndex >= router.children.length) {
    return
  }
  const branchSubflow = router.children[branchIndex]
  if (!branchSubflow) {
    return
  }

  const firstActionName = flowStructureUtil.getBranchFirstStepName(
    flowVersion,
    routerName,
    branchIndex,
  )
  if (firstActionName) {
    branchSubflow.connections.push({
      sourceId: router.name,
      targetId: firstActionName,
    })
  }
}

export { _deleteAction }
